import { useState } from 'react';
import {
  Alert,
  Button,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal,
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { VoidFunctionComponent } from 'react';
import type { Dispatch } from 'redux';

import PolicyModule from '../../redux/policies';

type ReduxProps = {
  importPolicy: (policy : {
    policyFile: File;
    title?: string | null;
    description?: string | null;
  }) => Promise<unknown>;
};

type Props = ReduxProps & {
  onHide(): void;
  show: boolean;
};

const ModalImportPolicy: VoidFunctionComponent<Props> = ({
  onHide, show, importPolicy,
}) => {
  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    if (!policyFile) {
      return;
    }

    try {
      await importPolicy({
        title,
        description,
        policyFile,
      });
    } catch (err) {
      setError((err as Error).message || (err as Error).toString());
      return;
    }
    setTitle('');
    setDescription('');
    setPolicyFile(null);
    setError(null);

    onHide();
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Import Policy From File</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error
          ? <Alert bsStyle="error">{error}</Alert>
          : null}
        <form className="form-horizontal" onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}>
          <FormGroup controlId="policyTitleForm">
            <div className="col-xs-3">
              <ControlLabel className="col-xs-3">Title (optional)</ControlLabel>
            </div>
            <div className="col-xs-9">
              <FormControl
                className="col-xs-9"
                type="text"
                placeholder="Mobile App Policy"
                value={title || ''}
                onChange={(ev: any) => {
                  setTitle(ev.target.value);
                }}
              />
            </div>
          </FormGroup>
          <FormGroup controlId="policyDescriptionForm">
            <div className="col-xs-3">
              <ControlLabel>Description (optional)</ControlLabel>
            </div>
            <div className="col-xs-9">
              <FormControl
                componentClass="textarea"
                placeholder="Standard policy for all mobile apps distributed by Company X."
                value={description || ''}
                onChange={(ev: any) => {
                  setDescription(ev.target.value);
                }}
              />
            </div>
          </FormGroup>
          <div>
            <p>Select a Policy json to import:</p>
            <input
              className="margin-top-lg"
              type="file"
              name="policy-to-import"
              accept=".json"
              onChange={(e) => {
                setPolicyFile(e.target?.files?.[0] || null);
              }}
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide}>Cancel</Button>
        <Button onClick={() => onSubmit()} bsStyle="primary">
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = createStructuredSelector({
  policyList: PolicyModule.selectors.getPolicyList,
});

//  redux-promise-middleware makes this `dispatch` return a promise. `async` puts that in the type,
//  so the modal can await it and show upload errors.
const mapDispatchToProps = (dispatch: Dispatch) => ({
  importPolicy: async (policy: Parameters<typeof PolicyModule.actions.import>[0]) =>
    dispatch(PolicyModule.actions.import(policy)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ModalImportPolicy);
